import { useState, useEffect } from "react";
import api from "../services/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { BarChart3, Globe, Truck, Users, Clock, Save, RotateCcw, Loader2, UserPlus } from "lucide-react";
import { toast } from "sonner";

export default function AdminStats() {
  const [overrides, setOverrides] = useState({ cities_covered: "", deliveries_completed: "", active_agents: "", on_time_rate: "", total_customers: "" });
  const [realStats, setRealStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/admin/stat-overrides"),
      api.get("/public/stats"),
    ]).then(([ovRes, statsRes]) => {
      const ov = ovRes.data || {};
      setOverrides({
        cities_covered: ov.cities_covered ?? "",
        deliveries_completed: ov.deliveries_completed ?? "",
        active_agents: ov.active_agents ?? "",
        on_time_rate: ov.on_time_rate ?? "",
        total_customers: ov.total_customers ?? "",
      });
      setRealStats(statsRes.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (overrides.cities_covered !== "") payload.cities_covered = parseInt(overrides.cities_covered) || 0;
      if (overrides.deliveries_completed !== "") payload.deliveries_completed = parseInt(overrides.deliveries_completed) || 0;
      if (overrides.active_agents !== "") payload.active_agents = parseInt(overrides.active_agents) || 0;
      if (overrides.on_time_rate !== "") payload.on_time_rate = parseFloat(overrides.on_time_rate) || 99.2;
      if (overrides.total_customers !== "") payload.total_customers = parseInt(overrides.total_customers) || 0;
      await api.put("/admin/stat-overrides", payload);
      toast.success("Homepage stats updated!");
      const { data } = await api.get("/public/stats");
      setRealStats(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    setSaving(true);
    try {
      await api.delete("/admin/stat-overrides");
      setOverrides({ cities_covered: "", deliveries_completed: "", active_agents: "", on_time_rate: "", total_customers: "" });
      toast.success("Overrides cleared — using real data only");
      const { data } = await api.get("/public/stats");
      setRealStats(data);
    } catch (err) {
      toast.error("Failed to clear");
    } finally {
      setSaving(false);
    }
  };

  const statFields = [
    { key: "cities_covered", label: "Cities Covered", icon: Globe, color: "text-blue-600", bg: "bg-blue-50", realKey: "cities_covered" },
    { key: "deliveries_completed", label: "Deliveries Completed", icon: Truck, color: "text-[#FF6B00]", bg: "bg-orange-50", realKey: "deliveries_completed" },
    { key: "active_agents", label: "Active Agents", icon: Users, color: "text-emerald-600", bg: "bg-emerald-50", realKey: "active_agents" },
    { key: "total_customers", label: "Total Customers", icon: UserPlus, color: "text-pink-600", bg: "bg-pink-50", realKey: "total_customers" },
    { key: "on_time_rate", label: "On-Time Rate (%)", icon: Clock, color: "text-violet-600", bg: "bg-violet-50", realKey: "on_time_rate" },
  ];

  if (loading) {
    return (
      <div className="p-16 text-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#FF6B00] mx-auto mb-3" />
        <p className="text-sm text-slate-400">Loading stats...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-stats-page">
      <div>
        <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-slate-900" data-testid="admin-stats-heading">Homepage Stats</h1>
        <p className="text-sm text-slate-500 mt-1">Control the numbers shown on the homepage. Boost values are added on top of real data.</p>
        <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800 max-w-2xl" data-testid="trusted-by-note">
          💡 The hero <strong>"Trusted by N+ users"</strong> pill reads <code>total_customers + active_agents</code>. Until that combined number crosses <strong>10</strong>, the pill shows <em>"We are coming soon"</em> instead. Boost <strong>Total Customers</strong> or <strong>Active Agents</strong> to get past 10 and the real number appears.
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {statFields.map((sf) => {
            const Icon = sf.icon;
            const realVal = realStats?.[sf.realKey] ?? 0;
            const boostVal = overrides[sf.key] !== "" ? Number(overrides[sf.key]) : 0;
            const isRate = sf.key === "on_time_rate";
            const displayVal = isRate ? (overrides[sf.key] !== "" ? overrides[sf.key] : realVal) : realVal;
            return (
              <div key={sf.key} className="border border-slate-200 rounded-xl p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <div className={`w-8 h-8 ${sf.bg} rounded-lg flex items-center justify-center`}>
                    <Icon className={`h-4 w-4 ${sf.color}`} />
                  </div>
                  <span className="text-sm font-semibold text-slate-800">{sf.label}</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex-1 space-y-1">
                    <Label className="text-[10px] text-slate-400 uppercase tracking-wider">
                      {isRate ? "Override Value" : "Boost Value"}
                    </Label>
                    <Input
                      type="number"
                      step={isRate ? "0.1" : "1"}
                      min="0"
                      placeholder={isRate ? "99.2" : "0"}
                      value={overrides[sf.key]}
                      onChange={(e) => setOverrides({ ...overrides, [sf.key]: e.target.value })}
                      className="rounded-lg border-slate-200 h-10 focus:border-[#FF6B00] focus:ring-[#FF6B00]/20"
                      data-testid={`stat-input-${sf.key}`}
                    />
                  </div>
                  <div className="text-center min-w-[80px]">
                    <p className="text-[10px] text-slate-400 uppercase tracking-wider">Shows As</p>
                    <p className={`text-xl font-bold ${sf.color}`} data-testid={`stat-display-${sf.key}`}>
                      {isRate ? `${displayVal}%` : `${displayVal}+`}
                    </p>
                  </div>
                </div>
                {!isRate && (
                  <p className="text-[10px] text-slate-400">
                    Real: {realVal - boostVal} + Boost: {boostVal} = <span className="font-bold text-slate-600">{displayVal}</span>
                  </p>
                )}
              </div>
            );
          })}
        </div>

        <div className="flex items-center gap-3 pt-2 border-t border-slate-100">
          <Button onClick={handleSave} disabled={saving} className="bg-[#FF6B00] text-white hover:bg-[#E65A00] rounded-xl px-6 font-semibold btn-press" data-testid="stats-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Save Changes
          </Button>
          <Button onClick={handleClear} disabled={saving} variant="outline" className="rounded-xl px-6 border-slate-200 text-slate-600" data-testid="stats-clear-btn">
            <RotateCcw className="h-4 w-4 mr-2" />
            Reset to Real Data
          </Button>
        </div>
      </div>
    </div>
  );
}
